import { DownloadIcon } from '@radix-ui/react-icons'
import { Container, Flex, Heading, Section, Select, Spinner, Table, Text } from '@radix-ui/themes'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { ClaimFormsServiceAPI } from '@API/data-services/claim-forms-service'
import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'
import { ArchiveDownloadState } from '@Components/claims/claim-form-pdf-types'
import { convertDateToIsoDate, formatDateToMonthDayYearString } from '@Components/util/utils'
import { downloadBlob } from '@Components/common/download-blob'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config/types'
import { RootState } from '@AppState/redux-store'
import '../styles.scss'

/** How many past days are offered for download. */
const DAYS_LISTED = 30

/**
 * Daily archive of claim form PDFs, one row per recent date.
 *
 * A sibling of WeeklyReport rather than a reuse: that component is week-oriented and hands the
 * browser a signed URL, whereas these bytes are fetched with the user's access token and saved as
 * a blob.
 *
 * Access requires read:reports AND read:claim-center-reservations; the route's requiredScopes and
 * the server both enforce that, so there is no in-component permission state to render.
 */
export const ClaimFormArchive = () => {
  const { currentUser, activeCustomerId } = useSelector((state: RootState) => state.users)
  const [rowStates, setRowStates] = useState<Record<string, ArchiveDownloadState>>({})
  const [claimCenterId, setClaimCenterId] = useState<string | null>(null)

  const { data: claimCenters } = useQuery({
    queryKey: [`${activeCustomerId}-claim-centers`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: () => ClaimReservationsServiceAPI.getClaimCenters(),
  })

  // Default to the operator's usual center, matching how the claims screen picks one.
  useEffect(() => {
    if (claimCenterId || !claimCenters?.length) {
      return
    }
    const preferred = claimCenters.find(
      (center) => center.id === currentUser?.metadata.defaultClaimCenterId,
    )
    setClaimCenterId((preferred ?? claimCenters[0]).id)
  }, [claimCenterId, claimCenters, currentUser?.metadata.defaultClaimCenterId])

  const dates = useMemo(() => {
    const today = new Date()
    return Array.from({ length: DAYS_LISTED }, (_, offset) => {
      const date = new Date(today)
      date.setDate(today.getDate() - (offset + 1))
      return date
    })
  }, [])

  const handleDownload = useCallback(
    async (date: Date) => {
      if (!claimCenterId) {
        return
      }

      const isoDate = convertDateToIsoDate(date)
      setRowStates((prev) => ({ ...prev, [isoDate]: ArchiveDownloadState.DOWNLOADING }))

      try {
        const archive = await ClaimFormsServiceAPI.downloadClaimFormArchive(claimCenterId, isoDate)

        if (!archive) {
          setRowStates((prev) => ({ ...prev, [isoDate]: ArchiveDownloadState.EMPTY }))
          return
        }

        downloadBlob(archive, `claim-forms-${isoDate}.zip`)
        setRowStates((prev) => ({ ...prev, [isoDate]: ArchiveDownloadState.IDLE }))
      } catch {
        setRowStates((prev) => ({ ...prev, [isoDate]: ArchiveDownloadState.ERROR }))
      }
    },
    [claimCenterId],
  )

  const onClaimCenterChange = (nextClaimCenterId: string) => {
    setClaimCenterId(nextClaimCenterId)
    // Per-row status refers to the previously selected center, so it would be misleading to keep.
    setRowStates({})
  }

  const renderStatus = (isoDate: string) => {
    const state = rowStates[isoDate] ?? ArchiveDownloadState.IDLE

    if (state === ArchiveDownloadState.EMPTY) {
      return (
        <Text size="1" color="gray">
          No claim forms received on this date
        </Text>
      )
    }
    if (state === ArchiveDownloadState.ERROR) {
      return (
        <Text size="1" color="red">
          Could not download this archive. Please try again.
        </Text>
      )
    }
    return null
  }

  return (
    <Container>
      <Section size="1">
        <Flex direction="column" gap="2">
          <Heading size="6">Claim Form Archive</Heading>
          <Text size="2" color="gray">
            A zip of every claim form PDF a claim center received on a given day. Archives are built
            each morning for the previous day.
          </Text>

          <Flex align="center" gap="2">
            <Text size="2">Claim center</Text>
            <Select.Root value={claimCenterId ?? undefined} onValueChange={onClaimCenterChange}>
              <Select.Trigger placeholder="Select a claim center" />
              <Select.Content>
                {(claimCenters ?? []).map((center) => (
                  <Select.Item key={center.id} value={center.id}>
                    {center.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
        </Flex>
      </Section>

      <Table.Root variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Date Received</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="download-cell">Download</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {dates.map((date) => {
            const isoDate = convertDateToIsoDate(date)
            const state = rowStates[isoDate] ?? ArchiveDownloadState.IDLE

            return (
              <Table.Row key={isoDate}>
                <Table.Cell className="report-description-cell">
                  {formatDateToMonthDayYearString(date)}
                </Table.Cell>
                <Table.Cell className="report-description-cell">{renderStatus(isoDate)}</Table.Cell>
                <Table.Cell className="download-cell">
                  <Flex
                    align="center"
                    className="download-cell-container"
                    onClick={() => {
                      if (state !== ArchiveDownloadState.DOWNLOADING) {
                        handleDownload(date)
                      }
                    }}>
                    {state === ArchiveDownloadState.DOWNLOADING ? (
                      <Spinner size="1" />
                    ) : (
                      <DownloadIcon />
                    )}
                  </Flex>
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
    </Container>
  )
}
